import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/shared/DatePicker';
import { isAfterWarsawToday } from '@/utils/warsawDate';
import { useSettlementApi } from '@/services/api';
import { formatPLN } from '@/utils/currency';
import { t } from '@/i18n';

export interface PaymentDialogTarget {
  invoiceId: string;
  invoiceNumber: string | null;
  currency: string | null;
  /** Outstanding balance in the invoice currency — prefills the amount. */
  outstanding: number | undefined;
}

interface RegisterPaymentDialogProps {
  companyNip: string;
  target: PaymentDialogTarget | null;
  onOpenChange: (open: boolean) => void;
  /** Called after a payment was saved — reload the list/detail. */
  onSaved: () => void;
}

/**
 * Task A3: "Zarejestruj wpłatę" dialog. Amount prefilled with the invoice's
 * outstanding balance, date defaults to today, note optional. The backend
 * allows overpayment but flags it — we surface that as a warning toast.
 */
export function RegisterPaymentDialog({
  companyNip,
  target,
  onOpenChange,
  onSaved,
}: RegisterPaymentDialogProps) {
  const [amount, setAmount] = useState<string>('');
  const [paidAt, setPaidAt] = useState<Date | null>(new Date());
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const { addPayment } = useSettlementApi();

  // Re-prime the form each time a new invoice is targeted.
  useEffect(() => {
    if (target) {
      setAmount(target.outstanding !== undefined && target.outstanding > 0 ? String(target.outstanding) : '');
      setPaidAt(new Date());
      setNote('');
    }
  }, [target]);

  const parsedAmount = amount === '' ? null : Number(amount);
  const amountInvalid = parsedAmount !== null && (!Number.isFinite(parsedAmount) || parsedAmount <= 0);
  const canSubmit = !saving && paidAt !== null && !amountInvalid && parsedAmount !== null;

  const handleSubmit = async () => {
    if (!target || !paidAt || parsedAmount === null || amountInvalid) return;
    try {
      setSaving(true);
      const result = await addPayment(companyNip, target.invoiceId, {
        amount: parsedAmount,
        paidAt: dayjs(paidAt).format('YYYY-MM-DD'),
        note: note.trim() || undefined,
      });
      if (result.overpayment) {
        toast.warning(t('sales.payments.overpaymentWarning'));
      } else {
        toast.success(t('sales.payments.success'));
      }
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(t('sales.payments.error'));
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={target !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('sales.payments.dialogTitle')}</DialogTitle>
          {target && (
            <DialogDescription>
              {t('sales.payments.dialogDescription', {
                invoiceNumber: target.invoiceNumber || target.invoiceId,
                outstanding:
                  target.outstanding !== undefined
                    ? `${formatPLN(target.outstanding)} ${target.currency || 'PLN'}`
                    : '—',
              })}
            </DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">
              {t('sales.payments.amountLabel', { currency: target?.currency || 'PLN' })}
            </Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={amountInvalid}
            />
            {amountInvalid && (
              <p className="text-destructive text-xs">{t('sales.payments.invalidAmount')}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">{t('sales.payments.dateLabel')}</Label>
            <DatePicker
              value={paidAt}
              onChange={setPaidAt}
              disabledDate={isAfterWarsawToday}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">{t('sales.payments.noteLabel')}</Label>
            <Input
              placeholder={t('sales.payments.notePlaceholder')}
              value={note}
              maxLength={1000}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={!canSubmit} onClick={handleSubmit}>
            {saving && <Loader2 className="size-4 animate-spin" />}
            {t('sales.payments.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
